// @ts-nocheck
/**
 * RangeSlider - 範囲選択スライダーコンポーネント
 *
 * 2つのハンドルで範囲を選択できる
 */

import React, { useState, useCallback, useRef } from 'react';
import { COLORS } from '../../styles/cardDexStyles';

/**
 * 範囲スライダー
 *
 * @param {Object} props
 * @param {number} props.min - 最小値
 * @param {number} props.max - 最大値
 * @param {number[]} props.value - 現在の値 [minValue, maxValue]
 * @param {Function} props.onChange - 変更時コールバック
 * @param {number} [props.step=1] - ステップ
 * @param {string} [props.label] - ラベル
 * @param {Function} [props.formatValue] - 値のフォーマット関数
 */
const RangeSlider = ({
  min = 0,
  max = 100,
  value = [0, 100],
  onChange,
  step = 1,
  label,
  formatValue = (v) => v.toLocaleString(),
}) => {
  const [isDragging, setIsDragging] = useState(null); // 'min' | 'max' | null
  const trackRef = useRef(null);

  const [minValue, maxValue] = value;

  // 値を範囲内に制限
  const clamp = (val, minLimit, maxLimit) => {
    return Math.min(Math.max(val, minLimit), maxLimit);
  };

  // 位置から値を計算
  const positionToValue = useCallback(
    (clientX) => {
      if (!trackRef.current) return min;
      const rect = trackRef.current.getBoundingClientRect();
      const ratio = (clientX - rect.left) / rect.width;
      const rawValue = min + ratio * (max - min);
      const steppedValue = Math.round(rawValue / step) * step;
      return clamp(steppedValue, min, max);
    },
    [min, max, step]
  );

  // ドラッグ開始
  const handleMouseDown = (handle) => (e) => {
    e.preventDefault();
    setIsDragging(handle);

    const handleMouseMove = (moveEvent) => {
      const newValue = positionToValue(moveEvent.clientX);

      if (handle === 'min') {
        const clampedValue = clamp(newValue, min, maxValue - step);
        onChange([clampedValue, maxValue]);
      } else {
        const clampedValue = clamp(newValue, minValue + step, max);
        onChange([minValue, clampedValue]);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // タッチ対応
  const handleTouchStart = (handle) => (e) => {
    e.preventDefault();
    setIsDragging(handle);

    const handleTouchMove = (moveEvent) => {
      const touch = moveEvent.touches[0];
      const newValue = positionToValue(touch.clientX);

      if (handle === 'min') {
        const clampedValue = clamp(newValue, min, maxValue - step);
        onChange([clampedValue, maxValue]);
      } else {
        const clampedValue = clamp(newValue, minValue + step, max);
        onChange([minValue, clampedValue]);
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(null);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  };

  // パーセンテージ計算
  const minPercent = ((minValue - min) / (max - min)) * 100;
  const maxPercent = ((maxValue - min) / (max - min)) * 100;

  return (
    <div style={{ marginBottom: '12px' }}>
      {/* ラベルと値表示 */}
      {label && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
          }}
        >
          <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>{label}</span>
          <span style={{ fontSize: '12px', color: COLORS.textPrimary }}>
            {formatValue(minValue)} - {formatValue(maxValue)}
          </span>
        </div>
      )}

      {/* スライダー */}
      <div
        ref={trackRef}
        style={{
          position: 'relative',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* トラック背景 */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: '4px',
            background: 'rgba(60, 60, 80, 0.8)',
            borderRadius: '2px',
          }}
        />

        {/* 選択範囲 */}
        <div
          style={{
            position: 'absolute',
            left: `${minPercent}%`,
            right: `${100 - maxPercent}%`,
            height: '4px',
            background: COLORS.accent,
            borderRadius: '2px',
          }}
        />

        {/* 最小ハンドル */}
        <div
          onMouseDown={handleMouseDown('min')}
          onTouchStart={handleTouchStart('min')}
          style={{
            position: 'absolute',
            left: `${minPercent}%`,
            transform: 'translateX(-50%)',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            background: isDragging === 'min' ? COLORS.accentLight : COLORS.accent,
            border: '2px solid white',
            cursor: 'grab',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            zIndex: isDragging === 'min' ? 2 : 1,
          }}
        />

        {/* 最大ハンドル */}
        <div
          onMouseDown={handleMouseDown('max')}
          onTouchStart={handleTouchStart('max')}
          style={{
            position: 'absolute',
            left: `${maxPercent}%`,
            transform: 'translateX(-50%)',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            background: isDragging === 'max' ? COLORS.accentLight : COLORS.accent,
            border: '2px solid white',
            cursor: 'grab',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            zIndex: isDragging === 'max' ? 2 : 1,
          }}
        />
      </div>

      {/* 最小/最大ラベル */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '10px',
          color: COLORS.textSecondary,
          marginTop: '4px',
        }}
      >
        <span>{formatValue(min)}</span>
        <span>{formatValue(max)}</span>
      </div>
    </div>
  );
};

export default RangeSlider;
